import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, BookOpen, Brain, TrendingUp, Cloud, Download, Users, Globe, Lock, MessageCircle, Tag, Moon, CheckCircle, Star, Send, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import LanguageSelector from "@/components/LanguageSelector";
import { usePWAInstall } from "@/components/PWAInstallPrompt";
import DreamMap from "@/components/DreamMap";
import FreeTrial from "@/components/FreeTrial";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canInstall, isInstalled, install, isIOS, isMobile } = usePWAInstall();
  const [feedbackForm, setFeedbackForm] = useState({ name: "", email: "", rating: 5, message: "" });
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackForm.message.trim()) return;
    setFeedbackLoading(true);
    try {
      await axios.post(`${API}/feedback`, { ...feedbackForm, page: "landing" });
      setFeedbackSent(true);
      toast.success(t('feedback.success') || 'Kiitos palautteestasi!');
    } catch {
      toast.error(t('feedback.error') || 'Palautteen lähetys epäonnistui');
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-green-100">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 overflow-visible relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-visible">
          <div className="flex justify-between items-center overflow-visible">
            <div className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Uneksia" className="w-36 h-36 sm:w-40 sm:h-40 -my-10 rounded-full shadow-lg" />
              <span className="text-2xl sm:text-3xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-6">
              <button
                onClick={() => navigate('/blog')}
                className="hidden sm:block text-sm font-body text-void/70 hover:text-void transition-colors"
                data-testid="blog-nav-link"
              >
                {t('blog.title')}
              </button>
              <button
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="hidden sm:block text-sm font-body text-void/70 hover:text-void transition-colors"
                data-testid="about-nav-link"
              >
                {t('nav.about') || 'Meistä'}
              </button>
              <div className="hidden sm:block">
                <LanguageSelector variant="minimal" />
              </div>
              <Button
                onClick={() => navigate('/auth')}
                className="bg-void text-white hover:bg-void/90 rounded-full px-4 sm:px-8 py-4 sm:py-6 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95 text-sm sm:text-base"
                data-testid="get-started-nav-button"
              >
                {t('landing.getStarted')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-[2fr_3fr] gap-12 items-center">
          {/* Left: Text Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 backdrop-blur-md rounded-full border border-white/60">
              <Sparkles className="w-4 h-4 text-lucid" />
              <span className="text-sm font-body text-void">{t('landing.tagline')}</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-light text-void tracking-tight leading-tight">
              {t('landing.heroTitle')}
              <span className="block italic text-ethereal text-glow">{t('landing.heroTitleItalic')}</span>
              {t('landing.heroTitle2')}
            </h1>
            
            <p className="text-lg text-void/70 font-body tracking-wide leading-relaxed">
              {t('landing.heroDescription')}
            </p>
            
            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={() => navigate('/auth')}
                className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-6 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                data-testid="get-started-hero-button"
              >
                {t('landing.startDreaming')}
              </Button>
              {isMobile && (
                <Button
                  onClick={() => {
                    if (canInstall) {
                      install();
                    } else if (isIOS) {
                      alert(t('pwa.iosInstructions', 'Napauta jakamispainiketta ja valitse "Lisää Koti-valikkoon"'));
                    } else {
                      alert(t('pwa.chromeInstructions', 'Napauta ⋮ (kolme pistettä) oikeassa yläkulmassa ja valitse "Asenna sovellus"'));
                    }
                  }}
                  variant="outline"
                  className="rounded-full px-6 py-6 font-body font-medium border-void/20 text-void hover:bg-void/5 transition-all duration-300"
                  data-testid="pwa-hero-install-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('pwa.downloadApp', 'Lataa sovellus')}
                </Button>
              )}
            </div>
          </div>

          {/* Right: Hero Image */}
          <div className="relative z-0">
            <picture>
              <source srcSet="/images/hero-dali.webp" type="image/webp" />
              <img
                src="/images/hero-dali.jpg"
                alt="Surreal dreamscape - Salvador Dalí inspired"
                className="rounded-3xl shadow-2xl"
                style={{ width: '100%', height: '500px', objectFit: 'cover', display: 'block' }}
                data-testid="hero-image"
              />
            </picture>
          </div>
        </div>
        {/* Scroll hint to free trial */}
        <div className="flex flex-col items-center mt-12 animate-bounce">
          <button
            onClick={() => document.getElementById('free-trial')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex flex-col items-center gap-1 text-void/50 hover:text-void/80 transition-colors cursor-pointer"
            data-testid="scroll-to-trial"
          >
            <span className="text-sm font-body">{t('trial.badge')}</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
      </section>

      {/* Free Trial Section */}
      <FreeTrial />

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-4">
            {t('landing.featuresTitle')} <span className="italic">{t('landing.featuresItalic')}</span>
          </h2>
          <p className="text-lg text-void/70 font-body tracking-wide">
            {t('landing.featuresSubtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Feature 1 */}
          <div className="glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 dream-card-hover" data-testid="feature-journal">
            <div className="w-14 h-14 bg-ethereal/20 rounded-2xl flex items-center justify-center mb-4">
              <BookOpen className="w-7 h-7 text-void" />
            </div>
            <h3 className="text-2xl font-heading font-light text-void mb-3">{t('landing.featureJournalTitle')}</h3>
            <p className="text-void/70 font-body leading-relaxed">
              {t('landing.featureJournalDesc')}
            </p>
          </div>

          {/* Feature 2 */}
          <div className="glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 dream-card-hover" data-testid="feature-analysis">
            <div className="w-14 h-14 bg-lucid/20 rounded-2xl flex items-center justify-center mb-4">
              <Brain className="w-7 h-7 text-void" />
            </div>
            <h3 className="text-2xl font-heading font-light text-void mb-3">{t('landing.featureAnalysisTitle')}</h3>
            <p className="text-void/70 font-body leading-relaxed">
              {t('landing.featureAnalysisDesc')}
            </p>
          </div>

          {/* Feature 3 */}
          <div className="glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 dream-card-hover" data-testid="feature-insights">
            <div className="w-14 h-14 bg-ethereal/20 rounded-2xl flex items-center justify-center mb-4">
              <TrendingUp className="w-7 h-7 text-void" />
            </div>
            <h3 className="text-2xl font-heading font-light text-void mb-3">{t('landing.featurePatternsTitle')}</h3>
            <p className="text-void/70 font-body leading-relaxed">
              {t('landing.featurePatternsDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section id="showcase" className="pt-20 pb-8 overflow-hidden" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-4">
              {t('landing.showcaseTitle') || 'Näin'} <span className="italic">{t('landing.showcaseItalic') || 'Uneksia toimii'}</span>
            </h2>
            <p className="text-lg text-void/70 font-body tracking-wide">
              {t('landing.showcaseSubtitle') || 'Tutustu sovelluksen ominaisuuksiin'}
            </p>
          </div>

          {/* Scrolling showcase */}
          <div className="relative">
            <div className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {[
                { src: "/images/showcase/dashboard.jpg", title: t('landing.showcaseDashboard') || "Dashboard", desc: t('landing.showcaseDashboardDesc') || "Seuraa uniputkeasi ja edistymistäsi" },
                { src: "/images/showcase/dreams.jpg", title: t('landing.showcaseDreams') || "Unipäiväkirja", desc: t('landing.showcaseDreamsDesc') || "Kirjoita, tallenna ja analysoi unesi" },
                { src: "/images/showcase/games.jpg", title: t('landing.showcaseGames') || "Unipelit", desc: t('landing.showcaseGamesDesc') || "Testaa tietämyksesi hauskoilla peleillä" },
                { src: "/images/showcase/quiz.jpg", title: t('landing.showcaseQuiz') || "Tietovisailu", desc: t('landing.showcaseQuizDesc') || "Kuinka paljon tiedät unista?" },
                { src: "/images/showcase/community.jpg", title: t('landing.showcaseCommunity') || "Yhteisö", desc: t('landing.showcaseCommunityDesc') || "Jaa uniasi muiden uneksijoiden kanssa" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 snap-center group"
                  style={{ width: 'min(280px, 75vw)' }}
                >
                  {/* Phone mockup frame */}
                  <div className="relative mx-auto" style={{ maxWidth: '260px' }}>
                    <div className="relative rounded-[2.5rem] border-[6px] border-void/90 bg-void/90 p-1.5 shadow-2xl group-hover:shadow-purple-200/50 transition-shadow duration-500">
                      {/* Notch */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-void/90 rounded-b-2xl z-10" />
                      {/* Screen */}
                      <div className="relative rounded-[2rem] overflow-hidden bg-white aspect-[9/19.5]">
                        <img
                          src={item.src}
                          alt={item.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Label */}
                  <div className="text-center mt-5">
                    <h3 className="font-heading text-void font-medium text-base">{item.title}</h3>
                    <p className="text-sm text-void/60 font-body mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Scroll hint gradient */}
            <div className="absolute right-0 top-0 bottom-8 w-16 bg-gradient-to-l from-white/80 to-transparent pointer-events-none sm:hidden" />
          </div>

          {/* Scroll dots indicator for mobile */}
          <div className="flex justify-center gap-2 mt-4 sm:hidden">
            {[0,1,2,3,4].map(i => (
              <div key={i} className="w-2 h-2 rounded-full bg-void/20" />
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="pt-8 pb-20 bg-gradient-to-b from-white/20 to-purple-50/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* About - One Clear Section */}
          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-6 text-center" data-testid="about-title">
              {t('about.title')}
            </h2>
            <p className="text-lg text-void/70 font-body leading-relaxed mb-8 text-center">
              {t('about.subtitle')}
            </p>
            <div className="space-y-4">
              {t('about.visionText').split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-void/70 font-body leading-relaxed text-center">
                  {paragraph}
                </p>
              ))}
            </div>
            
            {/* Privacy Promise */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/60 rounded-full border border-void/5">
                <Shield className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <span className="text-xs text-void/70 font-body">{t('about.privacyEncrypted') || 'Salattu yhteys (SSL/TLS)'}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/60 rounded-full border border-void/5">
                <Cloud className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <span className="text-xs text-void/70 font-body">{t('about.privacyCloud') || 'Unesi suojassa pilvessä'}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/60 rounded-full border border-void/5">
                <Lock className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <span className="text-xs text-void/70 font-body">{t('about.privacyPrivate') || 'Vain sinä näet unesi'}</span>
              </div>
            </div>

            <p className="text-void/50 font-heading text-base mt-6 text-center">— {t('about.visionAuthor')}</p>

            <div className="text-center mt-10 pt-8 border-t border-void/10">
              <h3 className="text-2xl sm:text-3xl font-heading font-light text-void mb-3">
                {t('landing.aboutCta') || 'Valmis sukeltamaan uniisi?'}
              </h3>
              <p className="text-void/60 font-body mb-6">
                {t('landing.aboutCtaSub') || 'Liity Unipäiväkirjaan tänään ja aloita itsesi löytämisen matka.'}
              </p>
              <Button
                onClick={() => navigate('/auth')}
                className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-3 font-body font-medium transition-all duration-300 hover:scale-105"
                data-testid="about-cta-button"
              >
                {t('landing.startDreaming')}
              </Button>
            </div>
          </div>

          {/* AI Analysis Deep Dive */}
          <div className="mb-16">
            <div className="glass-effect rounded-3xl p-8 sm:p-10 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-ethereal/20 rounded-2xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-void" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-heading font-light text-void">
                  {t('about.analysisTitle')}
                </h3>
              </div>
              <p className="text-void/70 font-body leading-relaxed mb-8">
                {t('about.analysisIntro')}
              </p>
              <div className="space-y-4">
                {[
                  { key: 'Meanings' },
                  { key: 'Symbols' },
                  { key: 'Emotions' },
                  { key: 'Psychology' },
                  { key: 'Archetypes' },
                  { key: 'Summary' },
                ].map(item => {
                  const label = t(`about.analysis${item.key}`);
                  return (
                  <div key={item.key} className="flex gap-4 items-start">
                    <span className="w-8 h-8 rounded-lg bg-ethereal/20 flex items-center justify-center text-base font-heading text-void/80 flex-shrink-0 mt-0.5">
                      {label.charAt(0).toUpperCase()}
                    </span>
                    <div>
                      <h4 className="font-body font-medium text-void text-sm">
                        {label}
                      </h4>
                      <p className="text-void/60 font-body text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: t(`about.analysis${item.key}Desc`) }}
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Dream Map Visualization */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-heading font-light text-void mb-3" data-testid="dreammap-title">
                {t('dreamMap.title') || 'Unikartta'}
              </h3>
              <p className="text-void/60 font-body max-w-xl mx-auto text-sm">
                {t('dreamMap.subtitle') || 'Tutustu unien teemojen ja tunteiden välisiin yhteyksiin. Vie hiiri solmujen päälle nähdäksesi piilotettuja yhteyksiä.'}
              </p>
            </div>
            <div className="glass-effect rounded-3xl p-4 sm:p-6">
              <DreamMap />
            </div>
          </div>

          {/* Community Section */}
          <div className="mb-16">
            <div className="glass-effect rounded-3xl p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-lucid/20 rounded-2xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-void" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-heading font-light text-void">
                  {t('about.communityTitle')}
                </h3>
              </div>
              <p className="text-void/70 font-body leading-relaxed mb-8">
                {t('about.communityIntro')}
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { key: 'PublicCircles', icon: Globe },
                  { key: 'PrivateCircles', icon: Lock },
                  { key: 'Commenting', icon: MessageCircle },
                  { key: 'Tags', icon: Tag },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <div key={item.key} className="flex gap-4">
                      <div className="w-10 h-10 bg-lucid/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-void/70" />
                      </div>
                      <div>
                        <h4 className="font-body font-medium text-void mb-1">
                          {t(`about.community${item.key}`)}
                        </h4>
                        <p className="text-void/60 font-body text-sm leading-relaxed">
                          {t(`about.community${item.key}Desc`)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

      {/* Blog Teaser Section */}
      <section className="py-20 bg-gradient-to-b from-white/30 to-purple-50/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-light text-void tracking-tight mb-4">
              {t('landing.blogTitle') || 'Sukella unien maailmaan'}
            </h2>
            <p className="text-lg text-void/70 font-body max-w-2xl mx-auto">
              {t('landing.blogSubtitle') || 'Blogistamme löydät kattavat artikkelit unista, unisymboleista, selkounista ja unien tieteestä.'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {[
              {
                title: t('landing.blogArticle1') || "Stressi ja unet",
                desc: t('landing.blogArticle1Desc') || "Näin mielen kuormitus muuttaa unimaailmaasi",
                slug: "stressi-ja-unet-nain-mielen-kuormitus-muuttaa-unimaailmaasi",
                icon: "💭"
              },
              {
                title: t('landing.blogArticle2') || "Unien muistaminen",
                desc: t('landing.blogArticle2Desc') || "5 askelta kohti kirkkaampia unimaailmoja",
                slug: "unien-muistaminen-5-askelta-kohti-kirkkaampia-unimaailmoja",
                icon: "🧠"
              },
              {
                title: t('landing.blogArticle3') || "Miksi näemme unia?",
                desc: t('landing.blogArticle3Desc') || "Tiedettä ja mystiikkaa yön tuntien takana",
                slug: "miksi-naemme-unia-tiedetta-ja-mystiikkaa-yon-tuntien-takana",
                icon: "🌙"
              },
            ].map((article, i) => (
              <div
                key={i}
                onClick={() => navigate(`/blog/${article.slug}`)}
                className="glass-effect rounded-2xl p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
              >
                <span className="text-2xl mb-3 block">{article.icon}</span>
                <h3 className="font-heading text-void font-medium text-base mb-2 group-hover:text-purple-700 transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-void/60 font-body leading-relaxed">
                  {article.desc}
                </p>
                <span className="inline-block mt-3 text-xs text-purple-500 font-body font-medium group-hover:translate-x-1 transition-transform">
                  {t('landing.readMore') || 'Lue artikkeli'} →
                </span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/blog')}
              variant="outline"
              className="rounded-full px-8 py-4 font-body font-medium border-void/20 hover:bg-void hover:text-white transition-all duration-300"
              data-testid="blog-cta-button"
            >
              {t('landing.viewAllArticles') || 'Kaikki artikkelit'} →
            </Button>
          </div>
        </div>
      </section>

          {/* Pricing Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-heading font-light text-void text-center mb-8" data-testid="pricing-title">
              {t('about.pricingTitle')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Free Plan */}
              <div className="glass-effect rounded-3xl p-8 border-2 border-transparent hover:border-ethereal/30 transition-all duration-300">
                <div className="text-center mb-6">
                  <h4 className="text-2xl font-heading font-light text-void mb-1">{t('about.pricingFreeTitle')}</h4>
                  <p className="text-sm text-void/60 font-body">{t('about.pricingFreeDesc')}</p>
                  <p className="text-3xl font-heading text-void mt-4">0 €</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {[1,2,3,4,5].map(i => (
                    <li key={i} className="flex items-center gap-2 text-void/70 font-body text-sm">
                      <CheckCircle className="w-4 h-4 text-lucid flex-shrink-0" />
                      {t(`about.pricingFreeFeature${i}`)}
                    </li>
                  ))}
                </ul>
                <p className="text-center text-xs text-void/50 font-body italic mb-4">
                  {t('about.freeTrialNote') || 'Aloita matka uniesi maailmaan ilman sitoutumista — ei pankkikorttia'}
                </p>
                <Button
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  className="w-full rounded-full py-5 font-body border-void/20 text-void hover:bg-void/5"
                  data-testid="try-free-button"
                >
                  {t('about.tryFree')}
                </Button>
              </div>

              {/* Premium Plan */}
              <div className="glass-effect rounded-3xl p-8 border-2 border-lucid/40 shadow-lg shadow-lucid/10 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-lucid text-void text-xs font-body font-medium px-3 py-1 rounded-bl-xl">
                  Premium
                </div>
                <div className="text-center mb-6">
                  <h4 className="text-2xl font-heading font-light text-void mb-1">{t('about.pricingPremiumTitle')}</h4>
                  <p className="text-sm text-void/60 font-body">{t('about.pricingPremiumDesc')}</p>
                  <p className="text-3xl font-heading text-void mt-4">{t('about.pricingMonthly')}</p>
                  <p className="text-sm text-void/50 font-body">{t('about.pricingYearly')}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {[1,2,3,4,5].map(i => (
                    <li key={i} className="flex items-center gap-2 text-void/70 font-body text-sm">
                      <CheckCircle className="w-4 h-4 text-lucid flex-shrink-0" />
                      {t(`about.pricingPremiumFeature${i}`)}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full bg-void text-white hover:bg-void/90 rounded-full py-5 font-body font-medium"
                  data-testid="get-premium-button"
                >
                  {t('about.getPremium')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - SEO Optimized */}
      <section className="py-20 bg-white/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-light text-void tracking-tight mb-4">
              {t('faq.title')}
            </h2>
            <p className="text-lg text-void/70 font-body">
              {t('faq.subtitle')}
            </p>
          </div>
          
          <div className="space-y-4">
            {/* FAQ Item 1 */}
            <details className="group glass-effect rounded-2xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-heading text-lg text-void list-none">
                {t('faq.q1')}
                <span className="ml-4 text-lucid group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-void/70 font-body leading-relaxed">
                {t('faq.a1')}
              </p>
            </details>
            
            {/* FAQ Item 2 */}
            <details className="group glass-effect rounded-2xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-heading text-lg text-void list-none">
                {t('faq.q2')}
                <span className="ml-4 text-lucid group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-void/70 font-body leading-relaxed">
                {t('faq.a2')}
              </p>
            </details>
            
            {/* FAQ Item 3 */}
            <details className="group glass-effect rounded-2xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-heading text-lg text-void list-none">
                {t('faq.q3')}
                <span className="ml-4 text-lucid group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-void/70 font-body leading-relaxed">
                {t('faq.a3')}
              </p>
            </details>
            
            {/* FAQ Item 4 */}
            <details className="group glass-effect rounded-2xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-heading text-lg text-void list-none">
                {t('faq.q4')}
                <span className="ml-4 text-lucid group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-void/70 font-body leading-relaxed">
                {t('faq.a4')}
              </p>
            </details>
            
            {/* FAQ Item 5 */}
            <details className="group glass-effect rounded-2xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-heading text-lg text-void list-none">
                {t('faq.q5')}
                <span className="ml-4 text-lucid group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-void/70 font-body leading-relaxed">
                {t('faq.a5')}
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Feedback Section - Before Footer */}
      <section className="py-16 bg-gradient-to-b from-white/10 to-purple-50/20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-effect rounded-3xl p-8 sm:p-10">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-heading font-light text-void tracking-tight mb-3">
                {t('feedback.title') || 'Anna palautetta'}
              </h2>
              <p className="text-void/70 font-body">
                {t('feedback.subtitle') || 'Mielipiteesi auttaa meitä kehittämään Uneksiaa paremmaksi'}
              </p>
            </div>

            {feedbackSent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-lucid/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-lucid" />
                </div>
                <h3 className="text-xl font-heading text-void mb-2">{t('feedback.thankYou') || 'Kiitos!'}</h3>
                <p className="text-void/70 font-body">{t('feedback.thankYouDesc') || 'Palautteesi on vastaanotettu. Se auttaa meitä tekemään Uneksiasta entistä paremman.'}</p>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className="space-y-5" data-testid="feedback-form">
                {/* Star Rating */}
                <div className="text-center">
                  <p className="text-sm font-body text-void/70 mb-2">{t('feedback.rateLabel') || 'Kuinka pidät Uneksiasta?'}</p>
                  <div className="flex justify-center gap-1">
                    {[1,2,3,4,5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedbackForm(p => ({ ...p, rating: star }))}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`feedback-star-${star}`}
                      >
                        <Star className={`w-7 h-7 ${star <= feedbackForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-void/20'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    placeholder={t('feedback.namePlaceholder') || 'Nimi (valinnainen)'}
                    value={feedbackForm.name}
                    onChange={e => setFeedbackForm(p => ({ ...p, name: e.target.value }))}
                    className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl h-12"
                    data-testid="feedback-name"
                  />
                  <Input
                    type="email"
                    placeholder={t('feedback.emailPlaceholder') || 'Sähköposti (valinnainen)'}
                    value={feedbackForm.email}
                    onChange={e => setFeedbackForm(p => ({ ...p, email: e.target.value }))}
                    className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl h-12"
                    data-testid="feedback-email"
                  />
                </div>

                <Textarea
                  placeholder={t('feedback.messagePlaceholder') || 'Kirjoita palautteesi tähän... Mitä pidät? Mitä voisi parantaa?'}
                  value={feedbackForm.message}
                  onChange={e => setFeedbackForm(p => ({ ...p, message: e.target.value }))}
                  className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl min-h-[120px]"
                  required
                  data-testid="feedback-message"
                />

                <div className="text-center">
                  <Button
                    type="submit"
                    disabled={feedbackLoading || !feedbackForm.message.trim()}
                    className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-5 font-body font-medium transition-all duration-300 hover:scale-105"
                    data-testid="feedback-submit"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {feedbackLoading ? (t('common.loading') || 'Lähetetään...') : (t('feedback.submit') || 'Lähetä palaute')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-void/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Uneksia" className="w-6 h-6" />
              <span className="font-heading text-void">{t('appName')}</span>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
              {/* Mobile language selector */}
              <div className="sm:hidden">
                <LanguageSelector variant="minimal" />
              </div>
              <button
                onClick={() => navigate('/blog')}
                className="text-sm text-void/60 hover:text-void font-body transition-colors cursor-pointer"
                data-testid="footer-blog-link"
              >
                {t('blog.title')}
              </button>
              <button
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm text-void/60 hover:text-void font-body transition-colors cursor-pointer"
                data-testid="footer-about-link"
              >
                {t('about.title')}
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="text-sm text-void/60 hover:text-void font-body transition-colors cursor-pointer"
                data-testid="footer-contact-link"
              >
                {t('contact.title')}
              </button>
              <a
                href="mailto:uneksia@unipäiväkirja.com"
                className="text-sm text-void/60 hover:text-void font-body transition-colors"
                data-testid="footer-email-link"
              >
                uneksia@unipäiväkirja.com
              </a>
              <button
                onClick={() => navigate('/privacy')}
                className="text-sm text-void/60 hover:text-void font-body transition-colors cursor-pointer"
                data-testid="footer-privacy-link"
              >
                {t('footer.privacy')}
              </button>
              <p className="text-sm text-void/60 font-body">
                {t('footer.copyright')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}